//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  WarehouseCustomerServiceGlobalStyle,
} from './WarehouseCustomerService.styles';

const hero = {
  slug: 'warehouse-customer-service',
  title: 'Warehouse Customer Service',
  description:
    'Answers the emails your warehouse gets all day about arrivals, receipts and ship dates. Opendock answers the appointment side and your WMS answers the inventory side. It books or moves appointments in Opendock and hands exceptions to a rep.',
};

const watchItRun = {
  title: 'A store wants to know where its order is',
  stageHeadTitle:
    'A store just emailed: did PO 4471 arrive, and when does our order ship?',
  stageHeadMeta: 'Customer Service · PO 4471',
  rows: [
    {
      time: '11:02:10',
      text: 'Email from a store manager about inbound PO 4471 and outbound order 88213.',
    },
    {
      time: '11:02:14',
      text: 'Opendock (appointment): PO 4471 checked in and unloaded at 10:40.',
    },
    {
      time: '11:02:18',
      text: 'WMS (inventory): 46 of 48 cartons received. Order 88213 picked, shipping tomorrow at 9am.',
    },
    {
      time: '11:02:25',
      text: 'Replied with the receipt, the ship date and the 2-carton shortage.',
    },
    {
      time: '11:02:30',
      text: 'Answered in 20 seconds. The shortage is with a rep, receipt and BOL attached.',
    },
  ],
  summaryText:
    'A store asked two questions that usually take two systems and a few minutes to answer. Loadsmart AI checked Opendock for the appointment and the WMS for the receipt and ship date, replied with the receipt and the ship date, and passed the carton shortage to a rep with everything needed to resolve it.',
  agentBadge: 'Customer Service Agent',
  escalationTitle: 'Problems go to a person, with the records attached.',
  escalationText:
    'The agent answers what your records can answer. A refused load, a quantity mismatch or an upset customer goes to one of your reps with the conversation and the records already pulled, so they can resolve it without searching the inbox first.',
};

const howItWorks = {
  stepsTitleWhite: 'Answers the routine questions,',
  stepsTitleGradient: 'and hands the problems to your team.',
  steps: [
    {
      title: 'Reads every request',
      description:
        'Works out what each email is asking, and which order, PO or appointment it is about. Email is the default; phone is a larger build you can add.',
    },
    {
      title: 'Answers from your systems',
      description:
        'Combines Opendock appointment and check-in status with WMS receipt, shortage and ship-date data, and replies with the facts. Receipts and ship dates need WMS access during setup.',
    },
    {
      title: 'Books appointments and updates ETAs',
      description:
        'Creates, reschedules or cancels an appointment when an email asks, and updates the ETA when a carrier sends a new one.',
    },
    {
      title: 'Escalates the exceptions',
      description:
        'Sends refused loads, shortages and complaints to a rep with the records attached.',
    },
  ],
  outcomes: [
    {
      title: 'Answers in seconds',
      description:
        'Routine status questions get a reply while the sender is still at their desk.',
    },
    {
      title: 'Reps work on the problems',
      description:
        'Your team spends its day on refused loads and shortages, the work that needs a person.',
    },
    {
      title: 'Every request is on record',
      description:
        'Each question, answer and appointment change is logged against the order or PO it concerns.',
    },
  ],
};

const runsOnYourStackNote =
  'Appointment, arrival and check-in status come from Opendock. Receipts, shortages and ship dates come from your WMS.';

const lineupCards = [
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Rebooks the appointment when a carrier runs late.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description: 'Supplies the live ETA behind every where-is-my-load answer.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description:
      'We warn the dock before free time runs out, and approve detention requests that match the gate record.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'dock-schedule-management',
    title: 'Dock Schedule Management',
    description:
      'We keep the dock schedule on plan: a briefing before the first shift, late arrivals moved to the next valid slot, and a reschedule plan ready when dock capacity drops.',
  },
  {
    slug: 'yard-task-orchestration',
    title: 'Yard Task Orchestration',
    description:
      'We select the asset for each pickup, schedule its preload and inspection, and return it to the yard, so it is ready when the carrier arrives.',
  },
];

function WarehouseCustomerService() {
  return (
    <PageWrapper>
      <WarehouseCustomerServiceGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack note={runsOnYourStackNote} />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Warehouse Customer Service" />
      <Footer />
    </PageWrapper>
  );
}

export default WarehouseCustomerService;
export { WarehouseCustomerService };
